import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useCrisp } from '../../hooks/useCrisp';
import ImpersonationBanner from '../impersonation/ImpersonationBanner';
import ImpersonationSearchModal from '../impersonation/ImpersonationSearchModal';
import { useImpersonation, getImpersonationTarget } from '../../hooks/useImpersonation';
import { BRAND } from '../../utils/brand';
import NoticeBell from '../notices/NoticeBell';
import NoticeBanner, { IMPERSONATION_DISMISS_REASON } from '../notices/NoticeBanner';
import { useNotices } from '../../hooks/useNotices';

interface Props {
  children: ReactNode;
}

export default function AppLayout({ children }: Props) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const { isImpersonating } = useImpersonation();
  const isSuperAdmin = user?.role === 'sa';
  const { notices, dismiss } = useNotices();
  const openNotices = notices.filter((n) => !n.dismissed);
  // Impersonating admins can read notices but not close them (the API also blocks it).
  // Also check sessionStorage directly: ImpersonationSearchModal has its own useImpersonation
  // instance (state isn't shared via context), so this component's isImpersonating can still
  // read stale (false) right after the modal starts impersonating.
  const canDismissNotices = !isImpersonating && !getImpersonationTarget();

  // Identify user with Crisp chat
  useCrisp(user);

  // Filter nav items based on user role
  // Only admin can access Users page
  // Admin and billing can access Billing page
  const canManageUsers = user?.role === 'admin';
  const canViewBilling = user?.role === 'admin' || user?.role === 'billing';

  const navItems = [
    { path: '/services', label: 'Services' },
    ...(canManageUsers ? [{ path: '/users', label: 'Users' }] : []),
    ...(canViewBilling ? [{ path: '/billing', label: 'Billing' }] : []),
    { path: '/settings', label: 'Settings' },
  ];

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      {/* Impersonation Search Modal */}
      <ImpersonationSearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />

      {/* Header */}
      <header className={`shadow-sm border-b border-gray-200 ${isImpersonating ? 'bg-amber-100' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/services" className="flex items-center space-x-2">
                <img
                  src="/logo.png"
                  alt={BRAND.name}
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold text-gray-900">
                  {BRAND.name}
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              <NoticeBell notices={notices} canDismiss={canDismissNotices} onDismiss={dismiss} />
              <span className="text-sm text-gray-600 hidden sm:block">
                {user?.email}
              </span>

              {/* Impersonation Search Button (SA only) */}
              {isSuperAdmin && (
                <button
                  onClick={() => setIsSearchModalOpen(true)}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                  title="Impersonate User"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                    />
                  </svg>
                </button>
              )}

              <button
                onClick={logout}
                className="px-3 sm:px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors whitespace-nowrap"
              >
                Sign Out
              </button>

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-md hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <svg
                  className="w-6 h-6 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium ${
                    isActive(item.path)
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              <div className="px-3 py-2 text-sm text-gray-500">
                {user?.email}
              </div>
            </div>
          </div>
        )}
      </header>

      {openNotices.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 space-y-3">
          {openNotices.map((n) => (
            <NoticeBanner
              key={n.id}
              notice={n}
              canDismiss={canDismissNotices}
              onDismiss={dismiss}
              collapsible
              dismissDisabledReason={IMPERSONATION_DISMISS_REASON}
            />
          ))}
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
            <div className="flex gap-4 text-sm">
              <a
                href={BRAND.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700"
              >
                Terms and Conditions
              </a>
              <a
                href={BRAND.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700"
              >
                Privacy Policy
              </a>
            </div>
            <span className="text-sm text-gray-500">
              {BRAND.name} {new Date().getFullYear()} &copy;
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
